import { useTranslate } from '@prenuvo/halo';

import {
  ActivityLog,
  NormaliseCheck as NormaliseCheckModel,
  ReportFindingConditionDetail,
} from '@/api';
import { useIsDDFeatureFlagEnabled } from '@/core/data-dog';
import { formatTimeStampToUTC } from '@/shared/util';

import { ActionClass, IGNORED_FIELDS } from '../activity-log.constant';
import { generateTitle } from '../activity-log.util';
import { ActivityLogDetail } from '../activity-log-detail.component';
import { ConditionDetail } from '../condition-detail.component';
import { CustomNormals } from './custom-normals.component';
import { NormaliseCheck } from './normalise-check.component';

interface ActivityLogUpdateProps {
  activityLog: ActivityLog;
}

export function ActivityLogUpdate({
  activityLog: { action, fullObject, objectDiff },
}: ActivityLogUpdateProps) {
  const translate = useTranslate();

  const isConditionTemplateDisplayNameEnabled = useIsDDFeatureFlagEnabled(
    'FCT_CONDITION_TEMPLATE_DISPLAY_NAME',
  );

  return (
    <div className="pt-2">
      {objectDiff &&
        Object.keys(objectDiff).map((item) => {
          if (IGNORED_FIELDS.includes(item)) {
            return null;
          }

          switch (item) {
            case 'conditionDetail':
              if (Array.isArray(objectDiff[item]?.new)) {
                return objectDiff[item].new.map((conditionDetail: ReportFindingConditionDetail) => (
                  <ConditionDetail
                    key={conditionDetail.name}
                    action={action as ActionClass}
                    conditionDetail={conditionDetail}
                    fieldName={conditionDetail.name}
                    fullObject={fullObject}
                  />
                ));
              }

              return null;

            case 'dateOfPrevious':
              if (objectDiff[item].new !== objectDiff[item].old) {
                return (
                  <ActivityLogDetail
                    key={item}
                    action={action as ActionClass}
                    fieldName={item}
                    oldValue={
                      typeof objectDiff[item].old === 'string' &&
                      formatTimeStampToUTC(objectDiff[item].old)
                    }
                    title={generateTitle(item, fullObject, isConditionTemplateDisplayNameEnabled)}
                    value={
                      typeof objectDiff[item].new === 'string' &&
                      formatTimeStampToUTC(objectDiff[item].new)
                    }
                  />
                );
              }

              return null;

            case 'normaliseCheck':
              return (
                <NormaliseCheck
                  key={item}
                  action={action as ActionClass}
                  item={item}
                  normaliseObject={objectDiff[item].new as NormaliseCheckModel}
                />
              );

            case 'customNormals':
              return (
                <CustomNormals
                  key={item}
                  action={action as ActionClass}
                  customNormalsObject={objectDiff[item].new as Record<string, string>}
                  item={item}
                  oldValue={objectDiff[item].old as Record<string, string>}
                />
              );

            case 's3Key':
              return (
                <ActivityLogDetail
                  key={item}
                  action={action as ActionClass}
                  fieldName={item}
                  oldValue={objectDiff[item].old && String(objectDiff[item].old)}
                  title={generateTitle(
                    translate('FCT.ACTIVITY_LOGS.PDF_PUBLISHED_LABEL') as string,
                    null,
                    isConditionTemplateDisplayNameEnabled,
                  )}
                  value={objectDiff[item].new && String(objectDiff[item].new)}
                />
              );

            case 'generatedPdfVersion':
              return (
                <ActivityLogDetail
                  key={item}
                  action={action as ActionClass}
                  fieldName={item}
                  title={generateTitle(item, null, isConditionTemplateDisplayNameEnabled)}
                  value={objectDiff[item].new && String(objectDiff[item].new)}
                />
              );

            default:
              return (
                <ActivityLogDetail
                  key={item}
                  action={action as ActionClass}
                  fieldName={item}
                  oldValue={objectDiff[item].old && String(objectDiff[item].old)}
                  title={generateTitle(item, null, isConditionTemplateDisplayNameEnabled)}
                  value={objectDiff[item].new && String(objectDiff[item].new)}
                />
              );
          }
        })}
    </div>
  );
}
